/**
Author - Brijesh Pandey
Git - https://bitbucket.org/__brijesh/
**/

import React, {useRef, useState, useCallback, useEffect} from 'react';
import styled from 'styled-components';
import {isMinor} from 'utils/dateUtils';
import {Spinner} from 'screens/commons';
import MuiForm from 'components/MuiForm';
import PopupModal from 'components/Popup';
import {ProgressBar} from 'components/ProgressBar';

import {triggerToast, WealthyValidations} from 'helpers';
import IMAGES from 'constants/Images';

export const relationDropdownOptions = [
  {
    label: 'Spouse',
    value: 'Spouse',
  },
  {
    label: 'Son',
    value: 'Son',
  },
  {
    label: 'Daughter',
    value: 'Daughter',
  },
  {
    label: 'Father',
    value: 'Father',
  },
  {
    label: 'Mother',
    value: 'Mother',
  },
  {
    label: 'Brother',
    value: 'Brother',
  },
  {
    label: 'Sister',
    value: 'Sister',
  },
  {
    label: 'Grand Son',
    value: 'Grand Son',
  },
  {
    label: 'Grand Daughter',
    value: 'Grand Daughter',
  },
  {
    label: 'Grand Father',
    value: 'Grand Father',
  },
  {
    label: 'Grand Mother',
    value: 'Grand Mother',
  },
  {
    label: 'Others',
    value: 'Others',
  },
];

export const MAJOR_ONLY_NOMINEE_RELATIONS = [
  'Spouse',
  'Father',
  'Mother',
  'Grand Father',
  'Grand Mother',
].map((relation) => relation.toLowerCase());

const MAX_NOMINEES = 3;

const makeCardSchema = (schema) => {
  if (!schema) return null;
  const cloned = structuredClone(schema);
  return cloned;
};

const createNomineeForm = (schema) => ({
  id: `${Date.now()}-${Math.random()}`,
  data: {},
  schema: makeCardSchema(schema),
});

const Footer = ({
  onSkip,
  isSkipLoading,
  skipButtonRef,
  handleSubmitAllNominees,
  isDisabled,
  isLoading,
  nomineeCount,
  ...props
}) => (
  <div className="footer">
    {props.previousBtn}
    {props.nextBtn}
    {props.submitBtn}

    <button
      className="submit-all-button"
      disabled={isDisabled || nomineeCount === 0 || isLoading}
      buttonText={`Submit ${nomineeCount} Nominee${nomineeCount !== 1 ? 's' : ''} and Continue`}
      onClick={(e) => {
        e.preventDefault();
        handleSubmitAllNominees();
      }}
      isLoading={isLoading}
    >
      {isLoading ? (
        <Spinner />
      ) : (
        `Submit ${nomineeCount} Nominee${nomineeCount !== 1 ? 's' : ''} and Continue`
      )}
    </button>

    <button
      ref={skipButtonRef}
      disabled={isSkipLoading}
      className="skip-button"
      onClick={onSkip}
      type="reset"
    >
      {isSkipLoading ? <Spinner color="blue" /> : `I don't wish to nominate now`}
    </button>

    <SkipHelperText>
      You can add or update your nominee details anytime from the Edit Profile section.
    </SkipHelperText>
  </div>
);

export const NomineeForm = ({onSubmit, isLoading, isDisabled, onSkip, isSkipLoading, schema}) => {
  const [nomineeForms, setNomineeForms] = useState(() => [createNomineeForm(schema)]);
  const skipButtonRef = useRef(null);

  useEffect(() => {
    if (!schema) return;
    setNomineeForms((prev) => {
      if (prev.length === 0) return [createNomineeForm(schema)];
      return prev.map((form) => (form.schema ? form : {...form, schema: makeCardSchema(schema)}));
    });
  }, [schema]);

  const handleNomineeFormChange = useCallback((index, newFormData) => {
    setNomineeForms((prev) =>
      prev.map((form, i) => (i === index ? {...form, data: newFormData || {}} : form))
    );
  }, []);

  const handleAddNomineeForm = () => {
    if (nomineeForms.length >= MAX_NOMINEES) return;
    const nextForm = createNomineeForm(schema);
    nextForm.schema?.fields?.forEach((field) => {
      if (field?.meta && field.name !== 'addressSelector') {
        field.meta.value = '';
      }
    });
    setNomineeForms((prev) => [...prev, nextForm]);
  };

  const handleRemoveNomineeForm = (index) => {
    setNomineeForms((prev) => {
      if (prev.length <= 1) {
        return [createNomineeForm(schema)];
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const hasFieldValue = (value) => {
    if (value === undefined || value === null || value === false) return false;
    if (typeof value === 'number') return !Number.isNaN(value);
    return String(value).trim() !== '';
  };

  const validateFields = (data) => {
    // Normalize and trim data fields
    data.name = data.name?.trim();
    data.phone_number = data.phone_number?.trim();
    data.email = data.email?.trim();
    data.pan_number = data.pan_number?.trim()?.toUpperCase();
    data.guardian_pan_number = data.guardian_pan_number?.trim()?.toUpperCase();
    data.guardian_aadhaar_number = data.guardian_aadhaar_number?.trim();

    // ======== 1. NAME, RELATION, SHARE, DOB VALIDATION (checked first) ========
    if (!hasFieldValue(data.name)) {
      triggerToast('Nominee name is required', 'error');
      return false;
    }

    const nomName = data.name;
    const isMinorSelected = hasFieldValue(data?.is_minor);
    const isMinorChecked = data?.is_minor === 'true' || data?.is_minor === true;

    if (!isMinorSelected) {
      triggerToast(`Please select whether ${nomName} is a minor`, 'error');
      return false;
    }

    if (!hasFieldValue(data.relation)) {
      triggerToast(`Relationship is required for ${nomName}`, 'error');
      return false;
    }

    if (!hasFieldValue(data.share) && data.share !== 0) {
      triggerToast(`Share percentage is required for ${nomName}`, 'error');
      return false;
    }

    const sharePercentage = parseFloat(data.share);
    if (Number.isNaN(sharePercentage) || sharePercentage < 10) {
      triggerToast(`Share percentage must be at least 10% for ${nomName}`, 'error');
      return false;
    }
    if (sharePercentage > 100) {
      triggerToast(`Share percentage cannot exceed 100% for ${nomName}`, 'error');
      return false;
    }

    if (isMinorChecked && !hasFieldValue(data.dob)) {
      triggerToast(`Date of birth is required for minor nominee ${nomName}`, 'error');
      return false;
    }

    if (hasFieldValue(data.dob)) {
      // Age range validation (0-100 years)
      const birthDate = new Date(data.dob);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (Number.isNaN(birthDate.getTime()) || age < 0 || age > 100) {
        triggerToast(`Invalid nominee age for ${nomName}`, 'error');
        return false;
      }

      if (isMinorChecked && !isMinor(data.dob)) {
        triggerToast(
          `Nominee ${nomName} DOB should be less than 18 years`,
          'error'
        );
        return false;
      }

      // Major-only relations check
      if (isMinor(data.dob) && MAJOR_ONLY_NOMINEE_RELATIONS.includes(data.relation.toLowerCase())) {
        const relationName = relationDropdownOptions.find(
          (n) => n.value.toLowerCase() === data.relation.toLowerCase()
        )?.label;
        triggerToast(
          `${relationName} relation is not allowed for Minor date of birth for ${nomName}`,
          'error'
        );
        return false;
      }
    }

    // ======== 2. PAN FORMAT (validate only if a value is provided) ========
    if (hasFieldValue(data.pan_number)) {
      const validatedPan = WealthyValidations.validateFormField.panGeneric(data.pan_number);
      if (!validatedPan) {
        triggerToast(`PAN is invalid for ${nomName}`, 'error');
        return false;
      }
    }

    // ======== 3. EXTRACT ID DETAILS AND USER DATA ========
    // All fields below belong to the "Optional Fields" section of the schema.
    // They are optional: skip validation entirely when empty, only validate format/logic when a value is provided.
    const nomineePan = data.pan_number;
    const nomineeAadhaar = data.aadhaar_number;
    const nomineePassport = data.passport_number;
    const nomineeIsNri = data.nominee_is_nri === 'true';

    // ======== 4. GUARDIAN DETAILS (optional; validate only if a value is provided) ========
    if (hasFieldValue(data.guardian_dob) && isMinor(data.guardian_dob)) {
      triggerToast(`Guardian cannot be a minor (must be adult) for ${nomName}`, 'error');
      return false;
    }

    if (hasFieldValue(data.guardian_pan_number)) {
      const validatedGuardianPan = WealthyValidations.validateFormField.panGeneric(
        data.guardian_pan_number
      );
      if (!validatedGuardianPan) {
        triggerToast(`Guardian PAN is invalid for ${nomName}`, 'error');
        return false;
      }
    }

    if (
      hasFieldValue(data.guardian_aadhaar_number) &&
      !/^[0-9]{4}$/.test(data.guardian_aadhaar_number)
    ) {
      triggerToast(
        `Please enter exactly last 4 digits of Aadhaar number for ${nomName}`,
        'error'
      );
      return false;
    }

    // ======== 5. NOMINEE ID DETAILS (optional; validate only if a value is provided) ========
    if (hasFieldValue(nomineeAadhaar) && !/^[0-9]{4}$/.test(nomineeAadhaar)) {
      triggerToast(
        `Please enter exactly last 4 digits of Aadhaar number for ${nomName}`,
        'error'
      );
      return false;
    }

    if (hasFieldValue(nomineePassport) && !/^[A-Z0-9]{6,15}$/i.test(nomineePassport)) {
      triggerToast(`Please enter valid passport number for ${nomName}`, 'error');
      return false;
    }

    // ======== 6. ADDRESS COMPLETENESS (required only when any address field is entered) ========
    const hasCity = hasFieldValue(data?.district_or_city);
    const hasPincode = hasFieldValue(data?.pincode);
    const hasCountry = hasFieldValue(data?.country);
    const hasLine1 = hasFieldValue(data?.line1);

    const hasAddressDetails = hasCity || hasPincode || hasCountry;
    const hasAddressDetailsExceptCountry = hasCity || hasPincode || hasLine1;

    // Address Line 1 is required when any address detail is entered
    if (hasAddressDetails && !hasLine1) {
      triggerToast('Address line 1 is required', 'error');
      return false;
    }

    // Country is required when line1, city or pincode is entered
    if (hasAddressDetailsExceptCountry && !hasCountry) {
      triggerToast('Country is required', 'error');
      return false;
    }

    // ======== 7. NRI nominee must select a country other than India (only if a country is provided) ========
    if (nomineeIsNri && hasFieldValue(data.country) && data.country.toLowerCase() === 'india') {
      triggerToast(`NRI nominees must select a country other than India for ${nomName}`, 'error');
      return false;
    }

    // ======== 8. Non-NRI nominee must have India as their country (only if a country is provided) ========
    if (!nomineeIsNri && hasFieldValue(data.country) && data.country.toLowerCase() !== 'india') {
      triggerToast(`Non-NRI nominees must have India as their country for ${nomName}`, 'error');
      return false;
    }

    // ======== 9. PHONE NUMBER (optional; validate format only if a value is provided) ========
    if (hasFieldValue(data.phone_number)) {
      if (!nomineeIsNri) {
        if (/[^0-9]/.test(data.phone_number)) {
          triggerToast('Phone number should not include special characters', 'error');
          return false;
        }
        if (!/^\d{10}$/.test(data.phone_number)) {
          triggerToast('Phone number must be exactly 10 digits', 'error');
          return false;
        }
      } else if (!/^\+?[0-9]{6,16}$/.test(data.phone_number)) {
        // NRI nominee phone number may include a country code
        triggerToast(
          'Please enter valid mobile number with country code. Cannot contain spaces and brackets',
          'error'
        );
        return false;
      }
    }

    // ======== 10. PINCODE (optional; validate format only if a value is provided) ========
    if (hasFieldValue(data.pincode) && !/^[0-9]{6}$/.test(data.pincode)) {
      triggerToast('Pincode must be exactly 6 digits', 'error');
      return false;
    }

    // ======== 11. EMAIL FORMAT (validate only if a value is provided) ========
    if (hasFieldValue(data.email) && !WealthyValidations.validateFormField.email(data.email)) {
      triggerToast(`Please enter valid email address for ${nomName}`, 'error');
      return false;
    }

    return true;
  };

  const validate_nominee = (formData, existingNominees, currentEditingIndex = null) => {
    // Filter out the nominee being edited from validation
    const nomineesToValidate = existingNominees.filter((_, idx) => idx !== currentEditingIndex);

    // 1. Validate nominee name is unique
    const newNomineeName = formData.name?.trim().toLowerCase();
    if (newNomineeName) {
      for (const nominee of nomineesToValidate) {
        const existingName = nominee.name?.trim().toLowerCase();
        if (existingName === newNomineeName) {
          triggerToast(
            `Duplicate nominee name: ${formData.name}. Each nominee must have a unique name`,
            'error'
          );
          return false;
        }
      }
    }

    // 2. Validate share will not exceed 100 when combining with existing nominees
    const existingTotalShare = nomineesToValidate.reduce((sum, nominee) => {
      const share = parseFloat(nominee.share) || 0;
      return sum + share;
    }, 0);

    const newShare = parseFloat(formData.share) || 0;
    const totalShare = existingTotalShare + newShare;

    if (totalShare > 100) {
      triggerToast(`Total share cannot exceed 100%. Current total: ${totalShare}%`, 'error');
      return false;
    }

    // 3. Validate nominee ID is unique
    const newNomineeIsMinor = isMinor(formData.dob);
    let newNomineeId = null;

    if (newNomineeIsMinor) {
      // For minors, use guardian ID
      if (formData.guardian_id_type === 'PAN' && formData.guardian_pan_number) {
        newNomineeId = formData.guardian_pan_number?.trim().toUpperCase();
      } else if (formData.guardian_id_type === 'AADHAAR' && formData.guardian_aadhaar_number) {
        newNomineeId = formData.guardian_aadhaar_number?.trim();
      }
    } else {
      // For majors, use nominee ID
      if (formData.nominee_id_type === 'PAN' && formData.pan_number) {
        newNomineeId = formData.pan_number?.trim().toUpperCase();
      } else if (formData.nominee_id_type === 'AADHAAR' && formData.aadhaar_number) {
        newNomineeId = formData.aadhaar_number?.trim();
      } else if (formData.nominee_id_type === 'PASSPORT' && formData.passport_number) {
        newNomineeId = formData.passport_number?.trim();
      }
    }

    if (newNomineeId) {
      for (const nominee of nomineesToValidate) {
        const isExistingMinor = isMinor(nominee.dob);
        let existingId = null;

        if (isExistingMinor) {
          if (nominee.guardian_id_type === 'PAN' && nominee.guardian_pan_number) {
            existingId = nominee.guardian_pan_number?.trim().toUpperCase();
          } else if (nominee.guardian_id_type === 'AADHAAR' && nominee.guardian_aadhaar_number) {
            existingId = nominee.guardian_aadhaar_number?.trim();
          }
        } else {
          if (nominee.nominee_id_type === 'PAN' && nominee.pan_number) {
            existingId = nominee.pan_number?.trim().toUpperCase();
          } else if (nominee.nominee_id_type === 'AADHAAR' && nominee.aadhaar_number) {
            existingId = nominee.aadhaar_number?.trim();
          } else if (nominee.nominee_id_type === 'PASSPORT' && nominee.passport_number) {
            existingId = nominee.passport_number?.trim();
          }
        }

        if (existingId && existingId === newNomineeId) {
          triggerToast(
            `Duplicate ID found: ${newNomineeId}. Each nominee must have a unique ID`,
            'error'
          );
          return false;
        }
      }
    }

    return true;
  };

  const cleanupIdFields = (data) => {
    const cleanedData = {...data};
    const isNomineeMinor = isMinor(data?.dob);

    // Cleanup nominee ID fields based on selected ID type (for major nominees)
    if (!isNomineeMinor) {
      if (cleanedData.nominee_id_type === 'PAN') {
        cleanedData.aadhaar_number = '';
        cleanedData.passport_number = '';
      } else if (cleanedData.nominee_id_type === 'AADHAAR') {
        cleanedData.pan_number = '';
        cleanedData.passport_number = '';
      } else if (cleanedData.nominee_id_type === 'PASSPORT') {
        cleanedData.pan_number = '';
        cleanedData.aadhaar_number = '';
      }
    } else {
      if (cleanedData.guardian_id_type === 'PAN') {
        cleanedData.guardian_aadhaar_number = '';
      } else if (cleanedData.guardian_id_type === 'AADHAAR') {
        cleanedData.guardian_pan_number = '';
      }
    }

    return cleanedData;
  };

  const handleSubmitAllNominees = () => {
    const validatedNominees = [];

    for (let i = 0; i < nomineeForms.length; i++) {
      const resolvedFormData = {...nomineeForms[i].data};

      if (!validateFields(resolvedFormData)) {
        return;
      }

      if (!validate_nominee(resolvedFormData, validatedNominees, null)) {
        return;
      }
    
      validatedNominees.push(cleanupIdFields(resolvedFormData));
    }

    if (validatedNominees.length <= 0) {
      triggerToast('Atleast one nominee is required.');
      return;
    }

    onSubmit(validatedNominees);
  };


  return (
    <Container>
      <MainContentWrapper>
        <FormSection>
          <FormSectionHeader>
            <FormSectionTitle>Add Nominee Details</FormSectionTitle>
            <FormSectionSubtitle>
              Please add a nominee below and you can add upto {MAX_NOMINEES} nominees in your
              profile
            </FormSectionSubtitle>
          </FormSectionHeader>

          {nomineeForms.map((form, index) => (
            <NomineeSchemaCard key={form.id}>
              <NomineeSchemaCardHeader>
                <NomineeSchemaCardTitle>Nominee {index + 1}</NomineeSchemaCardTitle>
                <RemoveNomineeButton
                  type="button"
                  onClick={() => handleRemoveNomineeForm(index)}
                  aria-label={`Remove nominee ${index + 1}`}
                >
                  <img src={IMAGES.crossIcon} alt="Close" width={14} height={14} />
                </RemoveNomineeButton>
              </NomineeSchemaCardHeader>
              <NomineeSchemaScroll>
                {form.schema && (
                  <MuiForm
                    schema={form.schema}
                    onChange={(_, data) => handleNomineeFormChange(index, data)}
                    data={form.data}
                    cacheId={form.id}
                    className="nominee-details-form"
                    wrapperClassName="meta-form-full-height"
                    title=""
                    sub_title=""
                    footer={() => null}
                    buttons={{}}
                    updateSchema={(result) => result}
                    fns={{isMinor}}
                  />
                )}
              </NomineeSchemaScroll>
            </NomineeSchemaCard>
          ))}

          {nomineeForms.length < MAX_NOMINEES && (
            <AddNomineeButton type="button" onClick={handleAddNomineeForm} aria-label="Add nominee">
              +
            </AddNomineeButton>
          )}

          <Footer
            onSkip={onSkip}
            isSkipLoading={isSkipLoading}
            skipButtonRef={skipButtonRef}
            handleSubmitAllNominees={handleSubmitAllNominees}
            isDisabled={isDisabled}
            isLoading={isLoading}
            nomineeCount={nomineeForms.length}
          />
        </FormSection>
      </MainContentWrapper>

      <PopupModal
        shouldShow={isSkipLoading}
        close={() => {}}
        hideCloseBtn={true}
        modalHeader={<></>}
      >
        <PopupContent>
          <ProgressBar
            waitTimeInSec={30}
            heading="Opting Out Nominee Step"
            subheading=""
            loadingTexts={[
              'Opting out nominee step...',
              'Processing your request...',
              'Almost done...',
              'Finalizing changes...',
            ]}
            loadingMsgIntervalInSec={2000}
            showShadow={false}
            footerTxt=""
          />
        </PopupContent>
      </PopupModal>
    </Container>
  );
};

const FormSection = styled.div`
  margin-top: 1rem;

  .nominee-details-form .input-container .Mui-disabled::after {
    content: none !important;
    display: none !important;
  }

  .nominee-details-form .mui-form-wrapper {
    padding: 0 !important;
    height: auto;
  }
`;

const FormSectionHeader = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;
`;

const FormSectionTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
`;

const FormSectionSubtitle = styled.p`
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
`;

const NomineeSchemaCard = styled.div`
  border: 1px solid #dee2e6;
  padding: 1.25rem;
  border-radius: 12px;
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
`;

const NomineeSchemaCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const NomineeSchemaCardTitle = styled.h4`
  margin: 0;
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 600;
`;

const RemoveNomineeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;

  img {
    display: block;
  }

  &:hover {
    background: #f3e8ff;
  }
`;

const NomineeSchemaScroll = styled.div`
  overflow-y: auto;
  padding-right: 0.25rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #6725f4;
    border-radius: 8px;
  }
`;

const AddNomineeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin: 0.5rem auto 1.5rem;
  padding: 0;
  border: 2px solid #6725f4;
  border-radius: 50%;
  background: #ffffff;
  color: #6725f4;
  font-size: 1.75rem;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: #f8f5ff;
  }
`;

const Container = styled.div`
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  height: 100vh; /* Use viewport height to ensure it takes full screen */

  .mui-form-wrapper {
    display: flex;
    flex-direction: column;
    padding-top: 0 !important;
    height: 100%;

    @media (min-width: 768px) {
      padding: 0 2rem 2rem 2rem;
    }
    .footer {
      margin-top: auto;
      padding-bottom: 3rem;
    }
  }

  [class*='mcol']:has(.nominee-relation-field) {
    flex-grow: 1;
    max-width: 100% !important;
  }

  .nominee-is-minor-field {
    div:has(> button) {
      justify-content: flex-start !important;
      max-width: max-content !important;
      margin-top: 0.5rem !important;
      padding: 0 !important;
    }

    button {
      flex: 0 0 6.5rem !important;
      width: 6.5rem !important;
      min-width: 0 !important;
      height: 2.25rem;
      margin-right: 1.8rem !important;
    }
  }

  .address-section-heading,
  .guardian-section-heading {
    color: ${(props) => props.theme.colors.text.primary};
    font-size: ${(props) => props.theme.typography.fontSize.sm};
    font-weight: bold !important;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    text-align: start;
    :before {
      content: '';
      color: ${(props) => props.theme.colors.text.primary};
    }
  }

  .optional-fields-heading {
    position: relative;
    width: 100%;
    margin: 0.75rem 0 0.25rem 0;

    .MuiFormControlLabel-root {
      display: flex;
      align-items: center;
      width: 100%;
      margin: 0;
      padding: 0.75rem 2.5rem 0.75rem 1rem;
      background: #f8f2ff;
      border-radius: 8px;
      border: 1px solid #e9d5ff;
      cursor: pointer;
      overflow: visible;
    }

    .MuiFormControlLabel-label {
      flex: 1;
      font-size: 1.1rem;
      font-weight: 700;
      color: #3e3d3d;
    }

    .MuiCheckbox-root {
      display: none !important;
    }

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      right: 1.25rem;
      z-index: 2;
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 7px solid #3e3d3d;
      transform: translateY(-50%);
      pointer-events: none;
      transition: transform 0.3s ease;
    }

    &:has(.Mui-checked)::after {
      transform: translateY(-50%) rotate(180deg);
    }
  }

  [class*='mcol']:has(.optional-field-item) {
    max-height: none;
    opacity: 1;
    overflow: visible;
    transform: translateY(0);
    pointer-events: auto;
    transition: max-height 0.35s ease, opacity 0.25s ease, transform 0.35s ease,
      margin 0.35s ease, padding 0.35s ease;
  }

  [class*='mcol']:has(.optional-fields-heading):not(:has(.Mui-checked))
    ~ [class*='mcol']:has(.optional-field-item) {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    transform: translateY(-6px);
    pointer-events: none;
  }

  .guardian-common-address-text,
  .guardian-info-text {
    margin-top: -1rem !important;
    :before {
      content: '';
    }
  }
  .guardian-contact-details-text {
    margin-top: -1rem !important;
  }

  .submit-all-button {
    margin-top: 1rem;
    width: 100%;
  }

  .skip-button {
    border: 1px solid ${(props) => props.theme.colors.brand.primary};
    color: ${(props) => props.theme.colors.brand.primary};
    background-color: transparent;
    margin-top: 1rem;
    height: 40px;
    width: 100%;
    border-radius: 52px;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.3s ease;

    &.highlighted {
      animation: highlight 3s ease-in-out;
    }

    &:hover:not(:disabled) {
      background-color: ${(props) => props.theme.colors.brand.secondary};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  @keyframes highlight {
    0%,
    100% {
      transform: scale(1);
      border-color: ${(props) => props.theme.colors.brand.primary};
      box-shadow: 0 0 0 0 rgba(139, 92, 246, 0);
    }
    25% {
      transform: scale(1.05);
      border-color: ${(props) => props.theme.colors.brand.tertiary};
      box-shadow: 0 0 0 8px rgba(139, 92, 246, 0.3);
    }
    50% {
      transform: scale(1.02);
      border-color: ${(props) => props.theme.colors.brand.tertiary};
      box-shadow: 0 0 0 5px rgba(139, 92, 246, 0.2);
    }
  }

  .submit-all-button {
    padding: 1rem 0;
    background: ${(props) => props.theme.colors.brand.primary};
    border-radius: 52px !important;
    font-weight: bold !important;
    width: 100%;
    margin-top: 1rem;
    color: #ffffff;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: scale 300ms;

    :disabled {
      background-color: rgb(236, 236, 236);
      color: rgb(161, 161, 161);
      pointer-events: none;
    }

    :hover {
      scale: 1.02;
    }
    .spinner-container {
      margin: 0rem auto;
    }
  }
`;

const MainContentWrapper = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 0 2rem 2rem 2rem;
`;

const TopSkipContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 1rem;

  @media (max-width: 767px) {
    padding: 0 2rem;
  }
`;

const TopSkipButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.brand.primary};
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0.5rem 0;
  font-weight: 500;

  &:hover {
    color: ${(props) => props.theme.colors.brand.tertiary};
  }

  &:focus {
    outline: none;
    color: ${(props) => props.theme.colors.brand.tertiary};
  }
`;

const SkipHelperText = styled.p`
  margin: 0.75rem 0 0;
  color: #6c757d;
  font-size: 0.8rem;
  text-align: center;
`;

const PopupContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0;
  border: 1px solid ${(props) => props.theme.colors.border.default};
  border-radius: ${({theme}) => theme.borderRadius?.md || '0.75rem'};
  .bar-card {
    border: none !important;
  }
`;
